import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useSWRMutation from 'swr/mutation';

import { Container, Label } from '@seuk/design-system/atoms';
import { Button, Checkbox, TextField } from '@seuk/design-system/molecules';
import { ErrorText } from '@seuk/design-system/molecules/text-field/text-field-styles';
import { showToastMessage, SmallMessagesToaster } from '@seuk/design-system/organisms';

import { Main as MainContainer } from '../../components';
import { ContactMethod } from '../../components/contact-method/contact-method';
import { ContactOptions } from '../../components/contact-options/contact-options';
import { Form, FormPageTitle, FormParagraph, FormSection, sendRequest } from '../../components/forms/forms';
import type { ConsultationFormValues } from '../../components/forms/types';
import { IndustrySector } from '../../components/industry-sector/industry-sector';
import { Privacy } from '../../components/privacy/privacy';

const INTERESTS = [
  'Biomethane',
  'Carbon capture and storage (CCS)',
  'Carbon credits',
  'Corporate power purchase agreements (PPAs)',
  'Electric vehicle charging',
  'Fleet solutions',
  'Hydrogen',
  'Lower-carbon fuels and lubricants',
  'On-site energy solutions',
  'Other - please specify',
] as const;

const INTEREST_OTHER = 'Other - please specify';

interface ConsultationFormProps {
  onSubmit: () => void;
}

const ConsultationForm = ({ onSubmit }: ConsultationFormProps) => {
  const methods = useForm<ConsultationFormValues>({
    mode: 'onChange',
    defaultValues: {
      interest: [],
      timeToCall: 'anytime',
    },
  });

  const errors = methods.formState.errors;

  const { trigger: submitForm, isMutating } = useSWRMutation('/api/consultation', sendRequest<ConsultationFormValues>);

  const onSubmitLocal = async (payload: ConsultationFormValues) => {
    try {
      await submitForm({
        ...payload,
        interest: payload.interest.filter((interest) => {
          return interest !== INTEREST_OTHER;
        }),
      });
      onSubmit();
    } catch (e) {
      showToastMessage({
        variant: 'small',
        type: 'danger',
        textMessage: 'Sorry, there has been a problem, please try again later',
      });
    }
  };

  const interestValue = methods.watch('interest');

  return (
    <FormProvider {...methods}>
      <Form as="form" onSubmit={methods.handleSubmit(onSubmitLocal)}>
        <TextField
          id="firstName"
          label="First name*"
          aria-required="true"
          {...methods.register('firstName', {
            required: true,
          })}
        />
        <TextField
          id="lastName"
          label="Surname*"
          aria-required="true"
          {...methods.register('lastName', {
            required: true,
          })}
        />
        <TextField
          id="company"
          label="Company*"
          aria-required="true"
          {...methods.register('company', { required: true })}
        />

        <ContactMethod methods={methods} />
        <IndustrySector methods={methods} />
        <div>
          <Label>Areas of interest*</Label>
          {INTERESTS.map((item) => {
            return (
              <Checkbox
                name="interest"
                key={item}
                label={item}
                value={item}
                {...methods.register('interest', {
                  validate: (value) => {
                    return value.length > 0 ? true : 'Please select your areas of interest';
                  },
                })}
              />
            );
          })}
          {errors.interest && <ErrorText>{errors.interest.message}</ErrorText>}
        </div>
        {interestValue.includes(INTEREST_OTHER) && (
          <TextField id="interestOther" label="Please specify" {...methods.register('interestOther')} />
        )}

        <ContactOptions methods={methods} />
        <Button
          type="submit"
          color="primary"
          disabled={!methods.formState.isDirty || !methods.formState.isValid || isMutating}
        >
          Submit
        </Button>
      </Form>
    </FormProvider>
  );
};

const Consultation = () => {
  const router = useRouter();
  const onSubmit = useCallback(() => {
    router.push('/consultation/thank-you');
  }, [router]);

  return (
    <MainContainer>
      <NextSeo title="Request a consultation" noindex={true} nofollow={true} />
      <FormSection>
        <Container>
          <FormPageTitle>Request a consultation</FormPageTitle>
          <FormParagraph>
            Complete this short form to discuss your lower-carbon energy requirements with a member of our team at a
            time that suits you.
          </FormParagraph>
          <FormParagraph>All fields marked with an (*) are mandatory</FormParagraph>
          <ConsultationForm onSubmit={onSubmit} />
          <FormParagraph>
            <Privacy />
          </FormParagraph>
        </Container>
      </FormSection>
      <SmallMessagesToaster variant="small" />
    </MainContainer>
  );
};

export default Consultation;
